import { Container, interaction } from "pixi.js";
import { clamp } from "@cc/maths-js";
import { Spring } from "wobble";
import { InteractionEvents } from "../ui/InteractionEvents";
import { CarouselOptions } from "./CarouselOptions";
import { CarouselAxis } from "./MovementAxis";
import { DefaultOptionsCarousel } from "./DefaultOptionsCarousel";
import { CarouselEvents } from "./CarouselEvents";
import { Timeout } from "../app/Timeout";

/**
 */
export class Carousel extends Container {
  /**
   */
  public static Axis: typeof CarouselAxis = CarouselAxis;
  public indexSnapThreshold: number = 0.1;
  public animateScale?: boolean = true;
  public animateAlpha?: boolean = true;
  public loopItems: boolean = false;
  public arrowNext?: Container;
  public arrowPrevious?: Container;
  private downIndex: number = 0;
  private index: number = 0;
  private spring: Spring;
  private animatedIndex: number = 0;
  private items: Container[];
  private indexOrder: number[] = [];
  private downOffset: number = 0;
  private pointerId: number = -1;
  private spacing: number = 0;
  private spacingOffset: number;
  private visibleItems: number = 1;
  private tapping: boolean;
  private tapTimeout: Timeout;

  /**
   *
   * @param options
   */
  constructor(options: CarouselOptions) {
    super();
    this.parseOptions(options);
    this.createInteractionEvents();
    this.createSpring();
    this.addButtonEvents();
    this.spacingOffset = this.spacing * -0.5;
    if (options.items && options.items.length > 0) {
      this.updatePosition();
    }
    for (let i = 0; i < this.items.length; i++) {
      this.indexOrder.push(i);
    }
    this.tapTimeout = new Timeout({
      duration: 0.25,
      cb: this.endTapTimeout,
      ctx: this
    });
  }

  /**
   */
  public destroy(options?: PIXI.DestroyOptions) {
    this.spring.stop();
    this.spring.removeAllListeners();
    super.destroy(options);
  }

  /**
   */
  private addButtonEvents() {
    if (this.arrowNext) {
      this.arrowNext.on(InteractionEvents.PointerTap, this.incrementIndex.bind(this, 1));
    }
    if (this.arrowPrevious) {
      this.arrowPrevious.on(InteractionEvents.PointerTap, this.incrementIndex.bind(this, -1));
    }
  }

  /**
   */
  private updateButtonVisibility() {
    if (!this.loopItems) {
      const index = Math.round(this.animatedIndex);
      if (this.arrowNext) {
        const disabled = index >= this.items.length - 1;
        this.arrowNext.alpha = disabled ? 0.5 : 1;
        this.arrowNext.interactive = !disabled;
      }
      if (this.arrowPrevious) {
        const disabled = index <= 0;
        this.arrowPrevious.alpha = disabled ? 0.5 : 1;
        this.arrowPrevious.interactive = !disabled;
      }
    }
  }

  /**
   */
  private refreshTapTimeout() {
    this.tapping = true;
    this.tapTimeout.start();
  }

  /**
   */
  private endTapTimeout() {
    this.tapping = false;
  }

  /**
   *
   * @param options
   */
  private parseOptions(options: CarouselOptions) {
    options = { ...DefaultOptionsCarousel, ...options };
    if (options.hitArea) this.hitArea = options.hitArea;
    Object.assign(this, options);
    this.addItems();
  }

  /**
   */
  private addItems() {
    for (const item of this.items) {
      if (item instanceof Container) this.addChild(item);
      else console.warn("Carousel - invalid item.");
    }
  }

  /**
   */
  private createSpring() {
    this.spring = new Spring({
      stiffness: 2000,
      damping: 1000,
      mass: 10
    });
    this.spring.onUpdate(s => {
      this.animatedIndex = s.currentValue;
      this.updatePosition();
    });
  }

  /**
   */
  public getIndex(): number {
    const index = this.modo(this.index, this.items.length);
    return this.indexOrder[index];
  }

  /**
   *
   * @param index
   * @param animate
   */
  public setIndex(index: number, dontAnimate?: boolean) {
    this.index = Math.round(index || 0);
    if (!this.loopItems) this.index = clamp(this.index, 0, this.items.length - 1);
    if (dontAnimate === true) {
      this.spring.updateConfig({ fromValue: this.index, toValue: this.index });
      this.animatedIndex = this.index;
      this.updatePosition();
    } else {
      this.spring.updateConfig({ toValue: this.index });
      this.spring.start();
    }
    if (Math.floor(index) > -1 && Math.ceil(index) < this.items.length) {
      this.emit(CarouselEvents.SetIndex, this.index, this.items[this.index]);
    }
  }

  /**
   */
  public incrementIndex(increment: number, dontAnimate?: boolean) {
    this.setIndex(this.index + increment, dontAnimate);
  }

  /**
   */
  public updatePosition() {
    for (let i = 0; i < this.items.length; i++) this.items[i].visible = false;

    const totalItems = 1 + this.visibleItems * 2;
    for (let i = 0; i < totalItems; i++) {
      const centeredIndex = this.animatedIndex + 0.5;
      const indexOffset = i - this.visibleItems;
      const index = Math.ceil(centeredIndex - this.visibleItems) + indexOffset;

      if (!this.loopItems) if (index < 0 || index >= this.items.length) continue;

      const wrappedIndex = this.modo(index, this.items.length);
      const phase = this.modo(-centeredIndex, 1);
      const item = this.items[wrappedIndex];
      item.x = (phase + indexOffset) * this.spacing + this.spacingOffset;
      item.visible = true;

      if (this.animateScale) {
        if (i === 0) {
          item.scale.set(phase * 0.75);
        } else if (i === totalItems - 1) {
          item.scale.set((-phase + 1) * 0.75);
        } else {
          item.scale.set(1 - Math.abs(phase - 0.5) * 0.5);
        }
      }

      if (this.animateAlpha) {
        if (i === 0) {
          item.alpha = phase;
        } else if (i === totalItems - 1) {
          item.alpha = -phase + 1;
        } else {
          item.alpha = 1;
        }
      }
    }

    this.updateButtonVisibility();
  }

  /**
   */
  private createInteractionEvents() {
    this.interactive = true;
    this.on(InteractionEvents.PointerMove, this.pointerMove, this);
    this.on(InteractionEvents.PointerDown, this.pointerDown, this);
    this.on(InteractionEvents.PointerOut, this.pointerUp, this);
    this.on(InteractionEvents.PointerUp, this.pointerUp, this);
    this.on(InteractionEvents.PointerUpOutside, this.pointerUp, this);
    this.on(InteractionEvents.PointerCancel, this.pointerUp, this);
  }

  /**
   *
   * @param e
   */
  private pointerMove(e: interaction.InteractionEvent) {
    if (e.data.identifier === this.pointerId) {
      const x = e.data.global.x;
      const offsetPosition = this.getOffsetPosition(x);
      this.spring.updateConfig({ toValue: offsetPosition });
      this.spring.start();
    }
  }

  /**
   *
   * @param e
   */
  private pointerDown(e: interaction.InteractionEvent) {
    if (this.pointerId === -1) {
      this.refreshTapTimeout();
      this.downIndex = this.index;
      this.pointerId = typeof e.data.identifier === "number" ? e.data.identifier : -1;
      this.downOffset = e.data.global.x;
    }
  }

  /**
   *
   * @param e
   */
  private pointerUp(e: interaction.InteractionEvent) {
    if (this.pointerId === e.data.identifier) {
      this.pointerId = -1;
      const pointerPosition = e.data.global;
      if (this.isTapping(e)) this.pointerTap();
      else {
        let offsetPosition = this.getOffsetPosition(pointerPosition.x);
        const dist = this.downIndex - offsetPosition;
        if (Math.abs(dist) > 1) offsetPosition = Math.round(offsetPosition);
        else if (this.downIndex - offsetPosition > this.indexSnapThreshold)
          offsetPosition = Math.floor(offsetPosition);
        else if (this.downIndex - offsetPosition < -this.indexSnapThreshold)
          offsetPosition = Math.ceil(offsetPosition);
        this.setIndex(offsetPosition);
        this.spring.updateConfig({ toValue: this.index });
        this.spring.start();
      }
    }
    this.tapTimeout.clear();
  }

  /**
   */
  private pointerTap() {
    const index = this.getIndex();
    this.emit(CarouselEvents.Select, index, this.items[index]);
  }

  /**
   */
  private getOffsetPosition(value: number) {
    const indexOffset = value / this.spacing;
    const downIndexOffset = this.downOffset / this.spacing;
    let position = this.index - indexOffset + downIndexOffset;
    if (!this.loopItems) position = clamp(position, -0.25, this.items.length - 0.75);
    return position;
  }

  /**
   */
  private isTapping(e: interaction.InteractionEvent) {
    return (
      this.tapping &&
      Math.abs(e.data.global.x - this.downOffset) < 8 &&
      this.items[this.getIndex()].getBounds().contains(e.data.global.x, e.data.global.y)
    );
  }

  /**
   */
  private modo(value: number, length: number): number {
    return ((value % length) + length) % length;
  }
}
